import TemplateMatchViz from "./TemplateMatchViz";

/**
 * DXR capability showcase — styled to match the natoe.ai design language
 * (Inter headings, DM Sans body, #2997FF→#00109D accent gradient, glass cards).
 *
 * Two parts:
 *   1. Personalization spotlight — DXR adapts to each radiologist (templates,
 *      phrasing, voice, modalities, preferences). The headline differentiator.
 *   2. Consolidated capability grid — every advantage, grouped into 6 cards.
 *
 * Claims are grounded in the DXR source (Augnito dictation + voice commands,
 * AI/Template modes, template upload/clone, TipTap editor, RTF/clipboard export,
 * report history, feedback, browser-based delivery, auth/security). We do NOT
 * claim prior-study retrieval — that is not in the product.
 */

// Personalization — focused on the two axes DXR personalizes most:
// your templates and your phrasing/structure.
const PERSONALIZATION = [
  {
    h: "Your templates, built in",
    p: "Bring the templates you already use — one at a time or a bulk import — or build them in DXR. Edit and organize them by modality.",
  },
  {
    h: "The right template, applied automatically",
    p: "DXR reads your dictation, recognizes the study and fills the matching template for you — no macro hunting. No template? DXR organizes your report.",
  },
  {
    h: "Your phrasing and structure",
    p: "Reports come out in your wording, tone and section order — Technique, Findings, Impression, BI-RADS — the way you would write them, every time.",
  },
];

export default function DxrCapabilities() {
  return (
    <section id="features" className="dxrcap">
      <style dangerouslySetInnerHTML={{ __html: styles }} />

      {/* ── Personalization spotlight ── */}
      <div className="dxrcap-spot">
        <div className="dxrcap-spot-text">
          <span className="dxrcap-eyebrow">Personalized to every radiologist</span>
          <h2 className="dxrcap-h2 dxrcap-h2-left">DXR adapts to how you report</h2>
          <p className="dxrcap-intro">
            Every radiologist reports differently. DXR molds to your templates and your phrasing —
            your structure, your wording, your tone — not the other way around.
          </p>
          <div className="dxrcap-spot-list">
            {PERSONALIZATION.map((t) => (
              <div key={t.h} className="dxrcap-spot-item">
                <span className="dxrcap-check" aria-hidden="true">✓</span>
                <div>
                  <h3>{t.h}</h3>
                  <p>{t.p}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Dynamic visual: dictation streams → DXR matches the template */}
        <TemplateMatchViz />
      </div>

    </section>
  );
}

const styles = `
.dxrcap { width: 90vw; margin: 0 auto; padding: 5rem 0; }

.dxrcap-eyebrow { display: inline-block; font-family: 'Inter', sans-serif; font-size: 13px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; background: linear-gradient(134.59deg, #2997FF -3.69%, #00109D 118.01%); -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent; margin-bottom: 16px; }
.dxrcap-h2 { font-family: 'Inter', sans-serif; font-weight: 400; font-size: clamp(30px, 4vw, 44px); line-height: 1.25; letter-spacing: 0; color: #fff; margin: 0 0 14px; }
.dxrcap-h2-center { text-align: center; }
.dxrcap-h2-left { text-align: left; }
.dxrcap-intro { font-family: 'DM Sans', sans-serif; font-size: 18px; line-height: 1.6; color: rgba(164,164,164,1); margin: 0 0 2.5rem; }
.dxrcap-intro-center { text-align: center; max-width: 680px; margin-left: auto; margin-right: auto; }

/* Spotlight */
.dxrcap-spot { display: grid; grid-template-columns: 1.05fr 0.95fr; gap: 56px; align-items: stretch; }
.dxrcap-spot-list { display: flex; flex-direction: column; gap: 22px; }
.dxrcap-spot-item { display: flex; gap: 14px; }
.dxrcap-check { flex: none; width: 26px; height: 26px; border-radius: 8px; background: linear-gradient(135deg, rgba(41,151,255,0.15), rgba(0,16,157,0.15)); border: 1px solid rgba(41,151,255,0.2); color: #2997FF; font-size: 13px; font-weight: 700; display: flex; align-items: center; justify-content: center; margin-top: 2px; }
.dxrcap-spot-item h3 { font-family: 'Inter', sans-serif; font-size: 18px; font-weight: 600; color: #fff; margin: 0 0 4px; }
.dxrcap-spot-item p { font-family: 'DM Sans', sans-serif; font-size: 15px; line-height: 1.6; color: rgba(164,164,164,0.85); margin: 0; }

/* Spotlight visual */
.dxrcap-spot-viz { position: relative; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); border-radius: 16px; padding: 28px; display: flex; flex-direction: column; justify-content: center; }
.viz-brand { position: absolute; top: 16px; right: 20px; font-size: 11px; font-weight: 700; letter-spacing: 0.14em; color: rgba(255,255,255,0.5); font-family: 'Inter', sans-serif; }
.viz-brand b { color: #2997FF; }
.viz-card { background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.1); border-radius: 12px; padding: 16px 18px; }
.viz-label { display: block; font-family: 'Inter', sans-serif; font-size: 11px; text-transform: uppercase; letter-spacing: 1.5px; color: rgba(255,255,255,0.45); margin-bottom: 8px; }
.viz-dict p { margin: 0; font-family: 'DM Sans', sans-serif; font-size: 14px; line-height: 1.55; color: #fff; }
.viz-arrow { display: flex; flex-direction: column; align-items: center; gap: 4px; padding: 16px 0; }
.viz-arrow span { font-family: 'Inter', sans-serif; font-size: 12px; font-weight: 600; color: #2997FF; }
.viz-templates { display: flex; flex-direction: column; gap: 10px; }
.viz-caret { display: inline-block; width: 2px; height: 1em; background: #2997FF; margin-left: 1px; vertical-align: text-bottom; animation: vizBlink 1s step-end infinite; }
@keyframes vizBlink { 50% { opacity: 0; } }
.viz-tpl { position: relative; display: flex; align-items: center; gap: 10px; background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.08); border-radius: 10px; padding: 12px 14px; font-family: 'DM Sans', sans-serif; font-size: 14px; color: rgba(255,255,255,0.6); transition: border-color 250ms ease, background 250ms ease, color 250ms ease; }
.viz-tpl-dot { width: 8px; height: 8px; border-radius: 50%; background: rgba(255,255,255,0.25); }
.viz-tpl.is-matched { border-color: rgba(41,151,255,0.5); background: rgba(41,151,255,0.08); color: #fff; font-weight: 600; }
.viz-tpl.is-matched .viz-tpl-dot { background: #2997FF; }
.viz-tpl-tag { margin-left: auto; font-family: 'Inter', sans-serif; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; color: #2997FF; }
.viz-sub { display: block; font-size: 11px; text-transform: uppercase; letter-spacing: 0.08em; color: rgba(255,255,255,0.45); margin-bottom: 2px; }
.viz-report { background: #0b0e14; border: 1px solid rgba(41,151,255,0.3); border-radius: 10px; padding: 14px 16px; animation: vizReveal 400ms ease; }
@keyframes vizReveal { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
.viz-report-chip { display: inline-block; font-family: 'Inter', sans-serif; font-size: 10.5px; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase; color: #2997FF; background: rgba(41,151,255,0.12); border-radius: 999px; padding: 3px 11px; margin-bottom: 10px; }
.viz-report p { margin: 5px 0; font-size: 13px; line-height: 1.5; font-family: 'DM Sans', sans-serif; }
.viz-report .lbl { color: #fff; font-weight: 700; text-transform: uppercase; font-family: 'Inter', sans-serif; }
.viz-report .b { color: #00D6FC; }
.viz-report .hl { color: #FFCC75; font-weight: 600; }
.viz-report .viz-rep-line { margin: 3px 0 3px 8px; }

/* Grid */
.dxrcap-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; margin-top: 3.5rem; }
.dxrcap-card { background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); border-radius: 16px; padding: 2.5rem 2rem; transition: border-color 0.3s ease, background 0.3s ease, box-shadow 0.3s ease; }
.dxrcap-card:hover { border-color: rgba(41,151,255,0.3); background: rgba(255,255,255,0.06); box-shadow: 0 4px 24px rgba(41,151,255,0.06); }
.dxrcap-card-icon { display: flex; align-items: center; justify-content: center; width: 56px; height: 56px; border-radius: 14px; background: linear-gradient(135deg, rgba(41,151,255,0.15), rgba(0,16,157,0.15)); border: 1px solid rgba(41,151,255,0.2); font-size: 24px; margin-bottom: 1.5rem; }
.dxrcap-card-h { font-family: 'Inter', sans-serif; font-size: 20px; font-weight: 600; line-height: 1.3; color: #fff; margin: 0 0 0.75rem; }
.dxrcap-card-body { font-family: 'DM Sans', sans-serif; font-size: 15px; line-height: 1.7; color: rgba(164,164,164,0.85); margin: 0; }

@media (max-width: 991px) {
  .dxrcap-grid { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 860px) {
  .dxrcap-spot { grid-template-columns: 1fr; gap: 40px; }
  .dxrcap-h2-left { text-align: center; }
}
@media (max-width: 576px) {
  .dxrcap { padding: 3rem 0; }
  .dxrcap-grid { grid-template-columns: 1fr; }
}
`;
